import { useSelector, shallowEqual } from "react-redux";
import { check } from "../../Can/utils";
import { NAV_SECTIONS } from "./navigationConfig";

/**
 * Computes whether a single nav item is visible for the current user.
 * A parent (has `children`) is visible if it has its own visible `route`,
 * OR at least one child is visible - never gated on a separate permission
 * of its own, so a child's own permission is never silently shadowed by an
 * unrelated parent-level check (see navigationConfig.js's header comment).
 */
function isVisible(item, ctx) {
  if (item.superOnly && !ctx.isSuper) return false;

  // Reserved for future plan-based feature gating - a no-op today since no
  // company has a restrictive plan yet (see companyContext.js's Plan seam).
  if (item.entitlement && ctx.entitlements && !ctx.entitlements.includes(item.entitlement)) {
    return false;
  }

  if (item.permission) {
    if (!check(ctx.permissions, ctx.userRoleName, item.permission)) {
      return false;
    }
  }

  if (item.children) {
    return item.children.some(child => isVisible(child, ctx));
  }

  return true;
}

function filterItem(item, ctx) {
  if (!isVisible(item, ctx)) return null;
  if (item.children) {
    const children = item.children.map(child => filterItem(child, ctx)).filter(Boolean);
    if (children.length === 0) return null;
    return { ...item, children };
  }
  return item;
}

/**
 * Central hook every nav-rendering component should use - do not
 * hand-roll permission checks in individual menu components, add/adjust
 * items in navigationConfig.js instead.
 */
export function useNavigation() {
  const { user } = useSelector(state => state.auth.user, shallowEqual);
  const permissions = user && user.userRole ? user.userRole.permission : [];
  const userRoleName = user && user.userRole ? user.userRole.name : null;
  const isSuper = !!(user && user.super);
  const entitlements = user && user.company && user.company.plan && user.company.plan.modules;

  const ctx = { permissions, userRoleName, isSuper, entitlements };

  return NAV_SECTIONS
    .map(section => ({ ...section, items: section.items.map(item => filterItem(item, ctx)).filter(Boolean) }))
    .filter(section => section.items.length > 0);
}
